import React from "react";
import {
  View,
  Text,
  Modal,
  Pressable,
  TouchableOpacity,
  StyleSheet,
  Platform,
  useWindowDimensions,
} from "react-native";
import Svg, { Path } from "react-native-svg";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useTheme } from "../../../theme";
import type { ThemeMode, ThemeColors } from "../../../theme";

export interface DeviceThemeModalProps {
  visible: boolean;
  onClose: () => void;
}

const THEME_OPTIONS: { mode: ThemeMode; label: string; icon: string; subLabel?: string }[] = [
  { mode: "light",  label: "Light",        icon: "light-mode" },
  { mode: "dark",   label: "Dark",         icon: "nightlight" },
  { mode: "system", label: "Match System", icon: "monitor", subLabel: "Follows your OS setting" },
];

/**
 * Device Theme selection popup.
 *
 * Reuses the existing centered-Modal/overlay/card pattern established by
 * LocationModal (transparent RN Modal, dimmed overlay, bordered elevated
 * card, top-right close icon) but is themed via the centralized
 * `useTheme()` semantic tokens so it renders correctly in Light, Dark, and
 * Match System. Selecting an option calls `setThemePreference()` directly
 * (no separate Redux access, no new persistence) and applies immediately.
 */
const DeviceThemeModal: React.FC<DeviceThemeModalProps> = ({
  visible,
  onClose,
}) => {
  const { preference, setThemePreference, colors } = useTheme();
  const { width: screenWidth } = useWindowDimensions();

  const modalWidth = Platform.OS === "web" ? 360 : screenWidth * 0.8;
  const styles = React.useMemo(() => createStyles(colors), [colors]);

  const handleSelect = (mode: ThemeMode) => {
    // Applies immediately, persists via Phase 0's existing themeSlice
    // persistence, and re-renders everywhere via ThemeProvider context.
    setThemePreference(mode);
    onClose();
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
      presentationStyle="overFullScreen"
    >
      <View style={styles.overlay}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />

        <View style={[styles.card, { width: modalWidth }]}> 
          {/* Title */}
          <View style={styles.titleRow}>
            <Text style={styles.title}>Device Theme</Text>
          </View>

          {/* Options */}
          <View style={styles.optionsList}>
            {THEME_OPTIONS.map((option) => {
              const isSelected = preference === option.mode;
              return (
                <TouchableOpacity
                  key={option.mode}
                  style={[
                    styles.optionRow,
                    isSelected && styles.optionRowSelected,
                  ]}
                  onPress={() => handleSelect(option.mode)}
                  accessibilityRole="radio"
                  accessibilityState={{ checked: isSelected }}
                  accessibilityLabel={option.label}
                >
                 

                  {/* Radio indicator */}
                  <View
                    style={[
                      styles.radioOuter,
                      isSelected && styles.radioOuterSelected,
                    ]}
                  >
                    {isSelected && <View style={styles.radioInner} />}
                  </View>

                   {/* Mode icon */}
                  <MaterialIcons
                    name={option.icon}
                    size={20}
                    color={isSelected ? colors.brand.primary : colors.text.secondary}
                    style={styles.optionIcon}
                  />

                  {/* Label + optional sub-label */}
                  <View style={styles.optionLabelGroup}>
                    <Text
                      style={[
                        styles.optionLabel,
                        isSelected && styles.optionLabelSelected,
                      ]}
                    >
                      {option.label}
                    </Text>
                  </View>

                  {/* Active badge */}
                  {isSelected && (
                    <View style={styles.activeBadge}>
                      <Text style={styles.activeBadgeText}>Active</Text>
                    </View>
                  )}
                </TouchableOpacity>
              );
            })}
          </View>
          {/* Done button */}
          <TouchableOpacity style={styles.doneButton} onPress={onClose}>
            <Text style={styles.doneButtonText}>Done</Text>
          </TouchableOpacity>

          {/* Close icon (top right) */}
          <TouchableOpacity
            style={styles.closeButton}
            onPress={onClose}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <Path
                d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                fill={colors.text.secondary}
              />
            </Svg>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    overlay: {
      flex: 1,
      backgroundColor: colors.overlay,
      justifyContent: "center",
      alignItems: "center",
    },
    card: {
      backgroundColor: colors.background.elevated,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.border.default,
      padding: 24,
      position: "relative",
      zIndex: 2,
      elevation: 12,
    },
    titleRow: {
      paddingRight: 24,
      marginBottom: 16,
    },
    title: {
      fontFamily: Platform.select({
        ios: "Inter",
        android: "Inter-Bold",
        default: "Inter, -apple-system, Roboto, Helvetica, sans-serif",
      }),
      fontWeight: "700",
      fontSize: 20,
      color: colors.text.primary,
    },
    optionsList: {
      gap: 4,
    },
    optionRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
      paddingVertical: 10,
      paddingHorizontal: 12,
      borderRadius: 8,
    },
    optionRowSelected: {
      backgroundColor: colors.background.page,
    },
    optionIcon: {
      // flex-shrink: 0 equivalent — icon never grows
      width: 20,
    },
    radioOuter: {
      width: 20,
      height: 20,
      borderRadius: 10,
      borderWidth: 1.4,
      borderColor: colors.border.default,
      justifyContent: "center",
      alignItems: "center",
      flexShrink: 0,
    },
    radioOuterSelected: {
      borderColor: colors.brand.primary,
    },
    radioInner: {
      width: 10,
      height: 10,
      borderRadius: 5,
      backgroundColor: colors.brand.primary,
    },
    optionLabelGroup: {
      flex: 1,
      gap: 2,
    },
    optionLabel: {
      fontSize: 15,
      color: colors.text.primary,
      fontFamily: Platform.select({
        ios: "Inter",
        android: "Inter-Regular",
        default: "Inter, -apple-system, Roboto, Helvetica, sans-serif",
      }),
    },
    optionLabelSelected: {
      fontWeight: "600",
      color: colors.brand.primary,
      fontFamily: Platform.select({
        ios: "Inter",
        android: "Inter-Bold",
        default: "Inter, -apple-system, Roboto, Helvetica, sans-serif",
      }),
    },
    optionSubLabel: {
      fontSize: 12,
      color: colors.text.secondary,
      fontFamily: Platform.select({
        ios: "Inter",
        android: "Inter-Regular",
        default: "Inter, -apple-system, Roboto, Helvetica, sans-serif",
      }),
    },
    activeBadge: {
      backgroundColor: colors.brand.primary,
      borderRadius: 99,
      paddingHorizontal: 8,
      paddingVertical: 2,
      flexShrink: 0,
    },
    activeBadgeText: {
      color: colors.text.inverse,
      fontSize: 11,
      fontWeight: "700",
      fontFamily: Platform.select({
        ios: "Inter",
        android: "Inter-Bold",
        default: "Inter, -apple-system, Roboto, Helvetica, sans-serif",
      }),
    },
    doneButton: {
      marginTop: 24,
      alignSelf: "flex-end",
      paddingVertical: 8,
      paddingHorizontal: 16,
      borderRadius: 4,
      backgroundColor: colors.brand.primary,
    },
    doneButtonText: {
      color: colors.text.inverse,
      fontSize: 14,
      fontWeight: "600",
    },
    closeButton: {
      position: "absolute",
      top: 16,
      right: 16,
    },
  });

export default DeviceThemeModal;
